import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
const API = window.__API_BASE + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const res = await axios.post(`${API}/auth/verify`, { token: storedToken });
          if (res.data.valid) {
            setUser(res.data.user);
            setToken(storedToken);
          } else {
            localStorage.removeItem('token');
            setToken(null);
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (e) {
          console.error('Token verification failed:', e.message);
          // Only clear on explicit 401/403 — keep token for network errors (server might be temporarily down)
          if (e.response?.status === 401 || e.response?.status === 403) {
            localStorage.removeItem('token');
            setToken(null);
            delete axios.defaults.headers.common['Authorization'];
          } else {
            // Network error / timeout — keep the token, assume still valid
            // Try to decode token locally to get user info
            try {
              const payload = JSON.parse(atob(storedToken.split('.')[1]));
              if (payload.exp * 1000 > Date.now()) {
                setUser({ email: payload.sub, id: payload.sub });
                setToken(storedToken);
              } else {
                localStorage.removeItem('token');
                setToken(null);
              }
            } catch { 
              // Keep token anyway — better to show stale than force re-login
              setToken(storedToken);
            }
          }
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  // Axios interceptor — only logout on explicit auth failures, NOT on network errors
  useEffect(() => {
    // Auth-failure detail strings raised by backend `get_current_user`
    // (dependencies.py) + legacy strings used by the older `auth.py` module.
    // Keep this list in sync with server-side HTTPException details.
    const AUTH_FAILURE_DETAILS = new Set([
      'Could not validate credentials', // legacy auth.py
      'Token expired',                  // legacy auth.py
      'Not authenticated',              // dependencies.py (missing Authorization header)
      'Invalid token',                  // dependencies.py (malformed JWT claim)
      'Invalid or expired token',       // dependencies.py (JWT verify failed / expired)
      'User not found',                 // dependencies.py (user deleted while session active)
    ]);
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only force logout on explicit 401 with a known auth-failure detail
        // (not network timeout, not permission-denied, not unrelated 401s).
        if (error.response?.status === 401 && error.response?.data?.detail && token) {
          const detail = error.response.data.detail;
          if (AUTH_FAILURE_DETAILS.has(detail)) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            // Surface a visible message so the user knows WHY they got kicked
            // out — otherwise they silently bounce back to login and retry
            // with the same dead token, loop forever.
            toast.error('Your session expired — please sign in again.', {
              id: 'session-expired',  // dedupe across multiple 401s
              duration: 5000,
            });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.success) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (e) {
      if (e.response?.data?.detail === 'pending_approval') {
        return { success: false, error: 'pending_approval' };
      }
      throw e;
    }
  };

  const register = async (email, password, name) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      if (res.data.pending_approval) {
        return { success: false, pending_approval: true, message: res.data.message };
      }
      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        return { success: true };
      }
      return { success: false, error: 'Registration failed' };
    } catch (e) {
      throw e;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
